import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

// Critical-path imports (sync) — layout shell, login, providers
import AdminLayout from "./components/AdminLayout";
import LoginPage from "./pages/LoginPage";
import { NotificationProvider } from "./context/NotificationContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeProvider } from "./context/ThemeContext";

// Lazy-loaded pages — each becomes a separate chunk
const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const AttendancePage = React.lazy(() => import("./pages/AttendancePage"));
const AttendanceDiscrepancyPage = React.lazy(() => import("./pages/AttendanceDiscrepancyPage"));
const TelegramAdminPage = React.lazy(() => import("./pages/TelegramAdminPage"));
const ImportStudentsPage = React.lazy(() => import("./pages/ImportStudentsPage"));
const StudentsPage = React.lazy(() => import("./pages/StudentsPage"));
const AddStudentPage = React.lazy(() => import("./pages/AddStudentPage"));
const StudentDetailPage = React.lazy(() => import("./pages/StudentDetailPage"));
const TeachersManagementPage = React.lazy(() => import("./pages/TeachersManagementPage"));
const TeacherDetailPage = React.lazy(() => import("./pages/TeacherDetailPage"));
const AddTeacherPage = React.lazy(() => import("./pages/AddTeacherPage"));
const GroupsPage = React.lazy(() => import("./pages/GroupsPage"));
const SchedulePage = React.lazy(() => import("./pages/SchedulePage"));
const CommentsPage = React.lazy(() => import("./pages/CommentsPage"));
const HomeworkPage = React.lazy(() => import("./pages/HomeworkPage"));
const HomeworkSubmissionsPage = React.lazy(() => import("./pages/HomeworkSubmissionsPage"));
const HomeworkTestPage = React.lazy(() => import("./pages/HomeworkTestPage"));
const IeltsEssayPage = React.lazy(() => import("./pages/IeltsEssayPage"));
const GroupHomeworkResultsPage = React.lazy(() => import("./pages/GroupHomeworkResultsPage"));
const PaymentManagementPage = React.lazy(() => import("./pages/PaymentManagementPage"));
const PaymentReportsPage = React.lazy(() => import("./pages/PaymentReportsPage"));
const KPIDashboardPage = React.lazy(() => import("./pages/KPIDashboardPage"));
const MessagingPage = React.lazy(() => import("./pages/MessagingPage"));
const StaffManagementPage = React.lazy(() => import("./pages/StaffManagementPage"));
const ExpendituresPage = React.lazy(() => import("./pages/ExpendituresPage"));
const ExamsPage = React.lazy(() => import("./pages/ExamsPage"));
const CertificatesPage = React.lazy(() => import("./pages/CertificatesPage"));
const TeacherPortalPage = React.lazy(() => import("./pages/TeacherPortalPage"));
const ParentPortalPage = React.lazy(() => import("./pages/ParentPortalPage"));
const ParentTelegramPage = React.lazy(() => import("./pages/ParentTelegramPage"));
const CentersPage = React.lazy(() => import("./pages/CentersPage"));
const MasterAdminPage = React.lazy(() => import("./pages/MasterAdminPage"));
const TeacherSalaryPage = React.lazy(() => import("./pages/TeacherSalaryPage"));
const AttendanceCallPage = React.lazy(() => import("./pages/AttendanceCallPage"));
const NotificationsPage = React.lazy(() => import("./pages/NotificationsPage"));
const TodoManagementPage = React.lazy(() => import("./pages/TodoManagementPage"));
const GradingPage = React.lazy(() => import("./pages/GradingPage"));
const AuditLogPage = React.lazy(() => import("./pages/AuditLogPage"));
const LibraryPage = React.lazy(() => import("./pages/LibraryPage"));
const SyllabusPage = React.lazy(() => import("./pages/SyllabusPage"));
const SyllabusTemplatesPage = React.lazy(() => import("./pages/SyllabusTemplatesPage"));
const TestBankPage = React.lazy(() => import("./pages/TestBankPage"));
const TestBuilderPage = React.lazy(() => import("./pages/TestBuilderPage"));
const WritingGraderPage = React.lazy(() => import("./pages/WritingGraderPage"));
const NpsPage = React.lazy(() => import("./pages/NpsPage"));
const AtRiskPage = React.lazy(() => import("./pages/AtRiskPage"));
const PlacementTestPage = React.lazy(() => import("./pages/PlacementTestPage"));
const PlacementTestTakingPage = React.lazy(() => import("./pages/PlacementTestTakingPage"));
const PlacementResultsPage = React.lazy(() => import("./pages/PlacementResultsPage"));
const PlacementQuestionsPage = React.lazy(() => import("./pages/PlacementQuestionsPage"));
const PrintMaterialsPage = React.lazy(() => import("./pages/PrintMaterialsPage"));
const VocabCheckPage       = React.lazy(() => import("./pages/VocabCheckPage"));
const ReadinessReportPage  = React.lazy(() => import("./pages/ReadinessReportPage"));
const SrsItemBankPage      = React.lazy(() => import("./pages/SrsItemBankPage"));
const SrsAnalyticsPage     = React.lazy(() => import("./pages/SrsAnalyticsPage"));
const SrsInterleavingPlannerPage = React.lazy(() => import("./pages/SrsInterleavingPlannerPage"));
const InstagramAnalyticsPage = React.lazy(() => import("./pages/InstagramAnalyticsPage"));
const AiBotPage = React.lazy(() => import("./pages/AiBotPage"));
const VocabularyGame   = React.lazy(() => import("./pages/VocabularyGame"));
const VocabularyTopics = React.lazy(() => import("./pages/VocabularyTopics"));
const FlashcardGame    = React.lazy(() => import("./pages/FlashcardGame"));
const TestGame         = React.lazy(() => import("./pages/TestGame"));
const PictionaryGame   = React.lazy(() => import("./pages/PictionaryGame"));
const BranchManagerPage = React.lazy(() => import("./pages/BranchManagerPage"));
const CompetitorsPage      = React.lazy(() => import("./pages/CompetitorsPage"));
const MarketPage           = React.lazy(() => import("./pages/MarketPage"));
const StudentSurveyPage    = React.lazy(() => import("./pages/StudentSurveyPage"));
const LeadsPage            = React.lazy(() => import("./pages/LeadsPage"));

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '2rem', fontFamily: 'sans-serif' }}>
          <h2 style={{ color: '#dc2626' }}>Something went wrong</h2>
          <p style={{ color: '#6b7280', marginBottom: '1rem' }}>{this.state.error.message}</p>
          <button onClick={() => { this.setState({ error: null }); window.location.href = '/login'; }}
            style={{ padding: '0.5rem 1rem', background: '#002147', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
            Back to Login
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const isAuthenticated = (() => { try { return !!localStorage.getItem("user"); } catch { return false; } })();
  const userRole = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}").role; } catch { return null; } })();
  const defaultRedirect =
    userRole === "MASTER_ADMIN"   ? "/master" :
    userRole === "BRANCH_MANAGER" ? "/branch-manager" :
    userRole === "TEACHER"        ? "/teacher" :
    userRole === "PARENT"         ? "/parent"  :
    "/dashboard";

  return (
    <ErrorBoundary>
    <ThemeProvider>
    <LanguageProvider>
    <NotificationProvider>
    <Router>
      <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/master" element={<MasterAdminPage />} />
        <Route path="/branch-manager" element={<BranchManagerPage />} />
        <Route path="/teacher" element={<TeacherPortalPage />} />
        <Route path="/parent" element={<ParentPortalPage />} />
        <Route path="/tg-parent" element={<ParentTelegramPage />} />
        <Route path="/placement/test/:testId" element={<PlacementTestTakingPage />} />
        <Route path="/vocabulary"              element={<VocabularyGame />}   />
        <Route path="/vocabulary/topics"     element={<VocabularyTopics />} />
        <Route path="/vocabulary/flashcards" element={<FlashcardGame />}    />
        <Route path="/vocabulary/test"       element={<TestGame />}         />
        <Route path="/vocabulary/pictionary" element={<PictionaryGame />}   />

        {/* Admin pages — all share the sidebar layout; redirect non-admins away */}
        <Route element={
          isAuthenticated && (userRole === "TEACHER" || userRole === "PARENT" || userRole === "MASTER_ADMIN" || userRole === "BRANCH_MANAGER")
            ? <Navigate to={defaultRedirect} replace />
            : <AdminLayout />
        }>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/attendance" element={<AttendancePage />} />
          <Route path="/attendance/calls" element={<AttendanceCallPage />} />
          <Route path="/attendance/discrepancies" element={<AttendanceDiscrepancyPage />} />
          <Route path="/telegram" element={<TelegramAdminPage />} />
          <Route path="/import" element={<ImportStudentsPage />} />
          <Route path="/students" element={<StudentsPage />} />
          <Route path="/leads" element={<LeadsPage />} />
          <Route path="/students/add" element={<AddStudentPage />} />
          <Route path="/students/:id" element={<StudentDetailPage />} />
          <Route path="/teachers" element={<TeachersManagementPage />} />
          <Route path="/teachers/add" element={<AddTeacherPage />} />
          <Route path="/teachers/:id" element={<TeacherDetailPage />} />
          <Route path="/teacher-salaries" element={<TeacherSalaryPage />} />
          <Route path="/groups" element={<GroupsPage />} />
          <Route path="/schedule" element={<SchedulePage />} />
          <Route path="/messages" element={<MessagingPage />} />
          <Route path="/staff" element={<StaffManagementPage />} />
          <Route path="/payments/reports" element={<PaymentReportsPage />} />
          <Route path="/payments" element={<PaymentManagementPage />} />
          <Route path="/expenditures" element={<ExpendituresPage />} />
          <Route path="/exams" element={<ExamsPage />} />
          <Route path="/certificates" element={<CertificatesPage />} />
          <Route path="/comments" element={<CommentsPage />} />
          <Route path="/homework" element={<HomeworkPage />} />
          <Route path="/homework/:id/test" element={<HomeworkTestPage />} />
          <Route path="/homework/:id/essay" element={<IeltsEssayPage />} />
          <Route path="/homework/:id/submissions" element={<HomeworkSubmissionsPage />} />
          <Route path="/homework/:id/results" element={<GroupHomeworkResultsPage />} />
          <Route path="/centers" element={<CentersPage />} />
          <Route path="/todos" element={<TodoManagementPage />} />
          <Route path="/grades" element={<GradingPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/audit-log" element={<AuditLogPage />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/syllabus" element={<SyllabusPage />} />
          <Route path="/syllabus/templates" element={<SyllabusTemplatesPage />} />
          <Route path="/test-bank" element={<TestBankPage />} />
          <Route path="/test-bank/:id/edit" element={<TestBuilderPage />} />
          <Route path="/test-bank/homework/:homeworkId/grade-writing" element={<WritingGraderPage />} />
          <Route path="/nps" element={<NpsPage />} />
          <Route path="/at-risk" element={<AtRiskPage />} />
          <Route path="/placement" element={<PlacementTestPage />} />
          <Route path="/placement/questions" element={<PlacementQuestionsPage />} />
          <Route path="/placement/results/:testId" element={<PlacementResultsPage />} />
          <Route path="/print-materials" element={<PrintMaterialsPage />} />
          <Route path="/vocab-checks" element={<VocabCheckPage />} />
          <Route path="/readiness-report" element={<ReadinessReportPage />} />
          <Route path="/srs-items" element={<SrsItemBankPage />} />
          <Route path="/srs-analytics" element={<SrsAnalyticsPage />} />
          <Route path="/srs-planner" element={<SrsInterleavingPlannerPage />} />
        </Route>

        {/* Market Intelligence — standalone (no AdminLayout sidebar), MASTER_ADMIN only */}
        <Route path="/competitors" element={
          !isAuthenticated
            ? <Navigate to="/login" replace />
            : userRole !== 'MASTER_ADMIN'
              ? <Navigate to={defaultRedirect} replace />
              : <CompetitorsPage />
        } />
        <Route path="/market" element={
          !isAuthenticated
            ? <Navigate to="/login" replace />
            : userRole !== 'MASTER_ADMIN'
              ? <Navigate to={defaultRedirect} replace />
              : <MarketPage />
        } />
        <Route path="/survey" element={
          !isAuthenticated
            ? <Navigate to="/login" replace />
            : userRole !== 'MASTER_ADMIN'
              ? <Navigate to={defaultRedirect} replace />
              : <StudentSurveyPage />
        } />

        {/* Instagram Analytics — standalone, MASTER_ADMIN only */}
        <Route
          path="/instagram-analytics"
          element={
            !isAuthenticated
              ? <Navigate to="/login" replace />
              : userRole !== 'MASTER_ADMIN'
                ? <Navigate to={defaultRedirect} replace />
                : <InstagramAnalyticsPage />
          }
        />

        {/* AI Bot — standalone, MASTER_ADMIN only */}
        <Route
          path="/ai-bot"
          element={
            !isAuthenticated
              ? <Navigate to="/login" replace />
              : userRole !== 'MASTER_ADMIN'
                ? <Navigate to={defaultRedirect} replace />
                : <AiBotPage />
          }
        />

        {/* KPI Dashboard — standalone, MASTER_ADMIN only */}
        <Route
          path="/kpi"
          element={
            !isAuthenticated
              ? <Navigate to="/login" replace />
              : userRole !== 'MASTER_ADMIN'
                ? <Navigate to={defaultRedirect} replace />
                : <KPIDashboardPage />
          }
        />

        <Route path="/" element={<Navigate to={isAuthenticated ? defaultRedirect : "/login"} replace />} />
        <Route path="*" element={<Navigate to={isAuthenticated ? defaultRedirect : "/login"} replace />} />
      </Routes>
      </Suspense>
    </Router>
    </NotificationProvider>
    </LanguageProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
